// ============================================================================
// FSA — Reset rounding for one Note (pure)
// ============================================================================
// THE rule for "re-round this note under the current factor". Used by:
//   • the Note Modal's "Reset rounding" button (FsaNoteModal), and
//   • the engagement-level "Re-round all" (fsa/engagement/reround/bulkReround)
// so the two can never disagree. See FSA-Note-Statement-Dataflow.md § 7
// "Changing the rounding factor mid-way".
//
// Scope — the whole note:
//   • classes + line items: rounded = actual under the current factor
//     (fresh, never the stored *_rounded_auto); factor-exempt classes
//     (share counts, face value) = actual; class with line items = Σ lines
//     (honouring a subtotal_formula); pl_flow lines keep the P&L's rounded;
//   • multi-column grid (PPE / ageing / Owners' Capital): input columns,
//     formula columns from the rounded cells, class = Σ lines, PY grid,
//     manual PY totals, column_totals; totals mirrored onto legacy fields;
//   • additional disclosures (fixed + variable grids).

import type { NoteClassData, NoteInboundPlFlow, NoteWorkspaceData } from 'api/types/fsa/noteData.types';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';
import { autoRound, isRoundingExemptMeta, plFlowRounded, withFreshAutoRounded } from './rounding';
import { evalClassSubtotal } from './subtotalFormula';
import { sumColumnTotals } from './columnTotals';
import { resetMultiColumnClassData, resetPriorYearTotalsRounding } from '../panes/multi-column/resetMultiColumnRounding';
import { resetFixedGridRounding, type FixedRow, type Row } from '../panes/disclosures/DisclosureFixedGrid';
import { resetVariableGridRounding, type ColumnSpec } from '../panes/disclosures/DisclosureVariableGrid';

export interface NoteRoundingContext {
  factor: string | null | undefined;
  isCreditBalance: boolean;
  columnSpecJson?: string | null;
  additionalDisclosuresSpecJson?: string | null;
  liveClasses: ReadonlyArray<{ classCode: string; metaDataJson?: string | null; subtotalFormula?: string | null }>;
}

export function resetNoteRounding(ws: NoteWorkspaceData, ctx: NoteRoundingContext): NoteWorkspaceData {
  const { factor } = ctx;
  const exemptCodes = new Set(ctx.liveClasses.filter(c => isRoundingExemptMeta(c.metaDataJson)).map(c => c.classCode));
  const formulaByCode = new Map(
    ctx.liveClasses.filter(c => c.subtotalFormula?.trim()).map(c => [c.classCode, c.subtotalFormula!.trim()]),
  );

  const next: NoteWorkspaceData = { ...ws, class_data: { ...ws.class_data } };
  for (const [code, rawCd] of Object.entries(ws.class_data ?? {})) {
    const cd = withFreshAutoRounded(rawCd, factor, exemptCodes.has(code));
    const lineItems = cd.line_items ?? [];
    if (lineItems.length === 0) {
      next.class_data[code] = { ...cd, amount_rounded: cd.amount_rounded_auto, prior_amount_rounded: cd.prior_amount_rounded_auto };
      continue;
    }
    // Displayed total = sum of displayed lines (§ 7 sum-of-lines invariant).
    const resetLineItems = lineItems.map(li => (li.allocation_mode === 'pl_flow'
      ? li // the P&L's saved rounded — adjust it on the P&L
      : { ...li, amount_rounded: li.amount_rounded_auto, prior_amount_rounded: li.prior_amount_rounded_auto }));
    const formula = formulaByCode.get(code);
    next.class_data[code] = {
      ...cd,
      line_items: resetLineItems,
      amount_rounded: evalClassSubtotal(resetLineItems, 'amount_rounded', formula),
      prior_amount_rounded: evalClassSubtotal(resetLineItems, 'prior_amount_rounded', formula),
    };
  }

  // Multi-column grid — the save copies column_values[<total>__rounded]
  // onto the legacy fields, so reset the grid itself and mirror back.
  let columns: FormatColumnSpec[] = [];
  try { columns = JSON.parse(ctx.columnSpecJson ?? '[]') as FormatColumnSpec[]; } catch { columns = []; }
  if (Array.isArray(columns) && columns.some(c => c.is_total)) {
    const totalCode = columns.find(c => c.is_total)?.code;
    const priorCode = columns.find(c => c.is_prior_year)?.code;
    const sign = ctx.isCreditBalance ? -1 : 1;
    const mirror = <T extends { column_values?: Record<string, number> | null; amount_rounded: number; prior_amount_rounded: number }>(row: T): T => {
      const cv = row.column_values;
      if (!cv) return row;
      const r = totalCode ? cv[totalCode + '__rounded'] : undefined;
      const pr = priorCode ? cv[priorCode + '__rounded'] : undefined;
      return {
        ...row,
        ...(typeof r === 'number' ? { amount_rounded: sign * r } : {}),
        ...(typeof pr === 'number' ? { prior_amount_rounded: sign * pr } : {}),
      };
    };
    const resetMc = resetMultiColumnClassData(next.class_data, columns, factor);
    for (const [code, cd] of Object.entries(resetMc)) {
      resetMc[code] = mirror({ ...cd, line_items: (cd.line_items ?? []).map(mirror) });
    }
    next.class_data = resetMc;
    next.prior_year_totals = resetPriorYearTotalsRounding(ws.prior_year_totals, factor) ?? ws.prior_year_totals;
    next.column_totals = sumColumnTotals(resetMc, columns);
  }

  // Additional disclosures.
  let specs: Array<{ code?: string; column_spec?: unknown; fixed_rows?: unknown }> = [];
  try {
    const parsed = JSON.parse(ctx.additionalDisclosuresSpecJson ?? '[]');
    specs = Array.isArray(parsed) ? parsed : [];
  } catch { specs = []; }
  if (specs.length > 0) {
    const nextDisc = { ...(ws.additional_disclosures ?? {}) };
    for (const s of specs) {
      const value = s.code ? ws.additional_disclosures?.[s.code] : undefined;
      if (!s.code || !Array.isArray(value) || !Array.isArray(s.column_spec)) continue;
      const cols = s.column_spec as ColumnSpec[];
      nextDisc[s.code] = Array.isArray(s.fixed_rows) && s.fixed_rows.length > 0
        ? resetFixedGridRounding(value as Row[], cols, s.fixed_rows as FixedRow[], factor)
        : resetVariableGridRounding(value as Row[], cols, factor);
    }
    next.additional_disclosures = nextDisc;
  }
  return next;
}

/**
 * Re-pull the saved `pl_flow` lines (Reserves profit) from the P&L's SAVED
 * values — actual and rounded, sign-bridged — and re-roll their classes.
 * Same values the Note Modal merge writes on open; the bulk re-round runs
 * this after the P&L has been re-saved under the new factor. Lines are only
 * updated, never added (adding a missing flow is the Note Modal's job).
 */
export function refreshPlFlowLines(
  ws: NoteWorkspaceData,
  flows: readonly NoteInboundPlFlow[],
  isCreditBalance: boolean,
  factor: string | null | undefined,
): NoteWorkspaceData {
  const sign: 1 | -1 = isCreditBalance ? -1 : 1;
  const next: NoteWorkspaceData = { ...ws, class_data: { ...ws.class_data } };
  for (const flow of flows) {
    if (flow.distribution !== 'SINGLE' || !flow.sourceIsSaved || !flow.targetClassCode) continue;
    const cd: NoteClassData | undefined = next.class_data[flow.targetClassCode];
    const localId = `pl::${flow.sourceReportCode}::${flow.sourceLineCode}`;
    if (!cd?.line_items?.some(li => li.local_id === localId)) continue;
    const { cy, py } = plFlowRounded(flow, sign, factor);
    const lineItems = cd.line_items.map(li => (li.local_id !== localId ? li : {
      ...li,
      amount_actual: (flow.amountCy ?? 0) * sign,
      prior_amount_actual: (flow.amountPy ?? 0) * sign,
      amount_rounded: cy, amount_rounded_auto: cy,
      prior_amount_rounded: py, prior_amount_rounded_auto: py,
    }));
    const sum = (f: 'amount_actual' | 'prior_amount_actual' | 'amount_rounded' | 'prior_amount_rounded') =>
      lineItems.reduce((s, li) => s + (li[f] ?? 0), 0);
    next.class_data[flow.targetClassCode] = {
      ...cd,
      line_items: lineItems,
      amount_actual: sum('amount_actual'),
      prior_amount_actual: sum('prior_amount_actual'),
      amount_rounded: sum('amount_rounded'),
      prior_amount_rounded: sum('prior_amount_rounded'),
      amount_rounded_auto: autoRound(sum('amount_actual'), factor),
      prior_amount_rounded_auto: autoRound(sum('prior_amount_actual'), factor),
    };
  }
  return next;
}
